/**
 * ⌘K / ⌘L omnibox (cmdk): jump to channels, people, agents, or run commands. Ranking uses the shared
 * Search.rank (same order on every platform). ↵ opens in the current tab, ⌘↵ in a new tab, ⇧↵ in a split.
 */
import { Access, Docs, Features, Search, Shell, Workspace, type OpenTarget } from "@bundle/core";
import type { IconName } from "@bundle/tokens";
import { Avatar, Icon, useFeatureAccess, usePageTree, useT, useWorkspace } from "@bundle/ui";
import { Command } from "cmdk";
import { useMemo, useRef, useState } from "react";

export type OmniCommand = { id: string; label: string; icon: IconName; run: () => void };

type Entry = {
  id: string;
  group: "pages" | "channels" | "people" | "agents" | "commands";
  label: string;
  sub?: string;
  icon?: IconName;
  /** A page's own icon (content). */
  emoji?: string;
  avatar?: { name: string; square: boolean };
  url?: string;
  run?: () => void;
};

type Props = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  navigate: (url: string, target: OpenTarget) => void;
  commands: OmniCommand[];
  canSplit: boolean;
};

const LIMIT = 8;

export function Omnibox(p: Props) {
  const t = useT();
  const ws = useWorkspace();
  const [query, setQuery] = useState("");
  const target = useRef<OpenTarget>("current");
  const cache = ws.state.cache;
  const docs = usePageTree().state.cache;
  const access = useFeatureAccess();

  const entries = useMemo<Entry[]>(() => {
    if (cache === undefined) return [];
    const now = ws.vm.deps.clock.now();
    const me = cache.members[cache.meId];
    const out: Entry[] = [];
    for (const id of Object.keys(cache.memberships)) {
      const c = cache.channels[id];
      if (c === undefined || c.archived || c.kind === "dm" || c.kind === "group_dm") continue;
      out.push({ id: `c:${c.id}`, group: "channels", label: c.name, sub: c.topic, icon: c.kind === "private" ? "lock" : "hash", url: Shell.formatUrl({ type: "channel", ws: ws.slug, channelId: c.id }) });
    }
    for (const m of Object.values(cache.members)) {
      if (m.deactivated || m.userId === cache.meId) continue;
      if (me !== undefined && !Access.canSeeMember(me, m, Workspace.sharedChannelIdsWith(cache, m.userId), now)) continue;
      const org = Access.organizationName(m);
      out.push({
        id: `u:${m.userId}`,
        group: m.kind === "agent" ? "agents" : "people",
        label: m.displayName,
        sub: m.kind === "agent" ? m.agent?.activity ?? m.agent?.description : org === undefined ? m.title : t("guest.badgeOrg", { org }),
        avatar: { name: m.displayName, square: m.kind === "agent" },
        url: Shell.formatUrl({ type: "dm", ws: ws.slug, userId: m.userId }),
      });
    }
    // Pages and databases by title; the path above them is the secondary text.
    for (const page of Object.values(docs?.pages ?? {})) {
      const path = Docs.ancestors(docs!, page.id).map((x) => x.title).filter((x) => x !== "").join(" / ");
      const e: Entry = {
        id: `p:${page.id}`,
        group: "pages",
        label: page.title === "" ? t("page.untitled") : page.title,
        icon: page.kind === "database" ? "board" : "page",
        url: page.kind === "database" ? Shell.formatUrl({ type: "db", ws: ws.slug, dbId: page.id }) : Shell.formatUrl({ type: "page", ws: ws.slug, pageId: page.id }),
      };
      if (path !== "") e.sub = path;
      if (page.icon !== undefined && !page.icon.startsWith("icon:")) e.emoji = page.icon;
      out.push(e);
    }
    for (const c of p.commands) out.push({ id: `cmd:${c.id}`, group: "commands", label: c.label, icon: c.icon, run: c.run });
    // Areas an admin switched off (core logic/features): their channels, DMs and pages aren't offered.
    return out.filter((e) => {
      const route = e.url === undefined ? undefined : Shell.parseUrl(e.url);
      const area = route === undefined ? undefined : Features.featureOfRoute(route);
      return area === undefined || access(area) !== "none";
    });
  }, [cache, docs, ws.slug, ws.vm, p.commands, t, access]);

  const ranked = useMemo(() => {
    const byId = new Map(entries.map((e) => [e.id, e]));
    const ids = Search.rank(query, entries.map((e) => ({ id: e.id, text: e.label, ...(e.sub === undefined ? {} : { alt: e.sub }) })), 60);
    const groups: Record<Entry["group"], Entry[]> = { pages: [], channels: [], people: [], agents: [], commands: [] };
    for (const id of ids) {
      const e = byId.get(id)!;
      if (groups[e.group].length < LIMIT) groups[e.group].push(e);
    }
    return groups;
  }, [entries, query]);

  const select = (e: Entry) => {
    p.onOpenChange(false);
    setQuery("");
    if (e.run !== undefined) e.run();
    else if (e.url !== undefined) p.navigate(e.url, target.current === "split" && !p.canSplit ? "current" : target.current);
  };

  const titles: Record<Entry["group"], string> = {
    pages: t("shell.omnibox.pages"),
    channels: t("shell.omnibox.channels"),
    people: t("shell.omnibox.people"),
    agents: t("shell.omnibox.agents"),
    commands: t("shell.omnibox.commands"),
  };

  return (
    <Command.Dialog
      open={p.open}
      onOpenChange={(o) => {
        p.onOpenChange(o);
        if (!o) setQuery("");
      }}
      label={t("shell.omnibox.placeholder")}
      shouldFilter={false}
      loop
      overlayClassName="omnibox-overlay"
      contentClassName="omnibox"
      onKeyDown={(e) => {
        target.current = e.metaKey || e.ctrlKey ? "newTab" : e.shiftKey ? "split" : "current";
      }}
    >
      <div className="omnibox-input">
        <Icon name="search" size="icon.md" color="text.tertiary" />
        <Command.Input value={query} onValueChange={setQuery} placeholder={t("shell.omnibox.placeholder")} data-testid="omnibox-input" />
        <kbd>esc</kbd>
      </div>
      <Command.List className="omnibox-list">
        <Command.Empty className="omnibox-empty">{t("shell.omnibox.noResults")}</Command.Empty>
        {(Object.keys(ranked) as Entry["group"][]).map((g) =>
          ranked[g].length === 0 ? null : (
            <Command.Group key={g} heading={titles[g]} className="omnibox-group">
              {ranked[g].map((e) => (
                <Command.Item
                  key={e.id}
                  value={e.id}
                  className="omnibox-item"
                  onSelect={() => select(e)}
                  onPointerDown={(ev) => {
                    target.current = ev.metaKey || ev.ctrlKey ? "newTab" : ev.shiftKey ? "split" : "current";
                  }}
                  data-testid={`omnibox-${e.id}`}
                >
                  <span className="omnibox-item-icon">
                    {e.emoji !== undefined ? (
                      <span className="omnibox-item-emoji">{e.emoji}</span>
                    ) : e.avatar === undefined ? (
                      <Icon name={e.icon ?? "hash"} size="icon.sm" />
                    ) : (
                      <Avatar name={e.avatar.name} square={e.avatar.square} size="avatar.xs" />
                    )}
                  </span>
                  <span className="omnibox-item-label">{e.label}</span>
                  {e.sub === undefined || e.sub === "" ? null : <span className="omnibox-item-sub">{e.sub}</span>}
                  {e.url === undefined ? null : <Icon name="chevronRight" size="icon.sm" color="text.tertiary" />}
                </Command.Item>
              ))}
            </Command.Group>
          ),
        )}
      </Command.List>
      <div className="omnibox-footer">{t("shell.omnibox.hint")}</div>
    </Command.Dialog>
  );
}
